/* eslint-disable react-refresh/only-export-components */
import { type DialogRootActions, Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'
import * as React from 'react'

import { buttonVariants } from '@eigtech/ui/components/button'
import { cn } from '@eigtech/ui/lib/utils'

type SheetContextType = {
  closeDisabled: boolean
}

const SheetContext = React.createContext<SheetContextType>({ closeDisabled: false })

export type SheetActions = DialogRootActions

export type SheetProps = React.ComponentProps<typeof SheetPrimitive.Root> &
  Partial<SheetContextType>

function Sheet({ closeDisabled = false, ...props }: SheetProps) {
  return (
    <SheetContext.Provider value={{ closeDisabled }}>
      <SheetPrimitive.Root data-slot="sheet" disablePointerDismissal {...props} />
    </SheetContext.Provider>
  )
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  const { closeDisabled } = React.useContext(SheetContext)

  return (
    <SheetPrimitive.Close
      className={cn(!props.render && buttonVariants({ variant: 'outline' }), className)}
      data-slot="sheet-close"
      disabled={closeDisabled}
      {...props}
    />
  )
}

function SheetAction({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return (
    <SheetPrimitive.Close
      className={cn(!props.render && buttonVariants(), className)}
      data-slot="sheet-action"
      {...props}
    />
  )
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetBackdrop({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Backdrop>) {
  return (
    <SheetPrimitive.Backdrop
      className={cn(
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-closed:animation-duration-[300ms] data-open:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className
      )}
      data-slot="sheet-overlay"
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  showBackdrop = true,
  showDismissButton = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Popup> & {
  showDismissButton?: boolean
  showBackdrop?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  const { closeDisabled } = React.useContext(SheetContext)

  return (
    <SheetPortal>
      {showBackdrop && <SheetBackdrop />}
      <SheetPrimitive.Popup
        className={cn(
          'bg-card data-open:animate-in data-closed:animate-out data-closed:duration-300 data-open:duration-500 fixed z-50 flex flex-col shadow-lg transition ease-in-out',
          side === 'right' &&
            'data-closed:slide-out-to-right data-open:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-2xl',
          side === 'left' &&
            'data-closed:slide-out-to-left data-open:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-2xl',
          side === 'top' &&
            'data-closed:slide-out-to-top data-open:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-closed:slide-out-to-bottom data-open:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className
        )}
        data-slot="sheet-content"
        {...props}
      >
        {children}

        {showDismissButton && (
          <SheetPrimitive.Close
            className="ring-offset-background focus:ring-ring data-open:bg-secondary rounded-xs focus:outline-hidden absolute right-4 top-4 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
            disabled={closeDisabled}
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-1.5 p-4', className)}
      data-slot="sheet-header"
      {...props}
    />
  )
}

function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex-1 overflow-auto px-4 py-2.5', className)}
      data-slot="sheet-body"
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse p-4 sm:flex-row sm:justify-end sm:space-x-2.5',
        className
      )}
      data-slot="sheet-footer"
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn('text-foreground font-semibold', className)}
      data-slot="sheet-title"
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="sheet-description"
      {...props}
    />
  )
}

const createHandle = SheetPrimitive.createHandle

export {
  createHandle,
  Sheet,
  SheetAction,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
}
